import { getBrowserFlags } from "@/misc/common";
import { scrollToElement } from "@/store";
import React from "react";

// First we need to add a type to let us extend the incoming component.
export type WithPaymentScrollProps = {
  completePaymentPaymentRef: (node: HTMLElement | null) => void;
  iOSSafari: boolean;
};
// Mark the function as a generic using P (or whatever variable you want)
export const withPaymentScroll = <P extends WithPaymentScrollProps>(
  Component: React.ComponentType<P>
) => {
  return class extends React.Component {
    private fired = false;
    private quietTimer: ReturnType<typeof setTimeout> | null = null;
    private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    private ro: ResizeObserver | null = null;

    constructor(props: any) {
      super(props);
    }

    componentWillUnmount() {
      this.cleanup();
      this.fired = true;
    }

    private cleanup = () => {
      if (this.quietTimer) {
        clearTimeout(this.quietTimer);
        this.quietTimer = null;
      }
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
      this.ro?.disconnect();
      this.ro = null;
    };

    completePaymentPaymentRef = (node: HTMLElement | null) => {
      if (node === null) {
        this.cleanup();
        return;
      }

      this.cleanup();
      this.fired = false;

      const fireOnce = () => {
        if (this.fired) return;
        this.fired = true;
        this.cleanup();
        // Target the "you must pay" header so the user sees context, not just
        // the payment widget. Falls back to the payment node if header absent.
        const target =
          (document.querySelector("[data-payment-target]") as HTMLElement | null) || node;
        scrollToElement({
          element: target,
          preventAutoscroll: false,
          scrollAfterStep: 0,
          initiatedByUser: true,
          priority: "payment",
        });
      };

      this.fallbackTimer = setTimeout(fireOnce, 3000);

      if (typeof ResizeObserver !== "undefined") {
        this.ro = new ResizeObserver(() => {
          if (this.quietTimer) clearTimeout(this.quietTimer);
          this.quietTimer = setTimeout(fireOnce, 300);
        });
        this.ro.observe(document.body);
        this.quietTimer = setTimeout(fireOnce, 300);
      } else {
        this.quietTimer = setTimeout(fireOnce, 1000);
      }
    };

    render() {
      const { iOSSafari } = getBrowserFlags();

      return (
        // @ts-ignore
        <Component
          iOSSafari={iOSSafari}
          completePaymentPaymentRef={this.completePaymentPaymentRef}
          {...this.props}
        />
      );
    }
  };
};
